import React, {useCallback, useEffect, useMemo, useState} from 'react';
import axios from 'axios';
import {endPoint} from '../../Helpers/EndPointHelpers';
import MessageComposer from './MessageComposer';
import ScheduledCommunications from './ScheduledCommunications';
import './CampaignManagerApp.scss';

const stageOrder = ['recruitment', 'enrollment', 'engagement', 'retention'];

const formatDate = (value) => {
  if(!value) return 'No activity recorded';

  return new Intl.DateTimeFormat('en', {
    month:'short', day:'numeric', year:'numeric', hour:'numeric', minute:'2-digit'
  }).format(new Date(value));
};

const CampaignManagerApp = () => {
  const requestedCompanyId = useMemo(() => new URLSearchParams(window.location.search).get('company_id'), []);
  const [session, setSession] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(requestedCompanyId || '');
  const [activeSection, setActiveSection] = useState('pathway');
  const [pathway, setPathway] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [messageRuns, setMessageRuns] = useState(null);
  const [scheduledItems, setScheduledItems] = useState(null);
  const [composeLoading, setComposeLoading] = useState(false);
  const [composeNotice, setComposeNotice] = useState('');
  const [composeRefresh, setComposeRefresh] = useState(0);
  const [stage, setStage] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [campaignSearchInput, setCampaignSearchInput] = useState('');
  const [campaignSearch, setCampaignSearch] = useState('');
  const [scheduledSearchInput, setScheduledSearchInput] = useState('');
  const [scheduledFilters, setScheduledFilters] = useState({channel:'', source:'', status:'', search:''});
  const [loading, setLoading] = useState(true);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [scheduledLoading, setScheduledLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRequestError = useCallback((requestError) => {
    const status = requestError?.response?.status;

    if(status === 401){
      const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.assign(`/login?return_to=${returnTo}`);
      return;
    }

    setError(status === 403 ? 'Your HSC account does not have Campaign Manager access.' : 'Campaign Manager could not load. Please try again.');
    setLoading(false);
    setCampaignsLoading(false);
    setComposeLoading(false);
    setScheduledLoading(false);
  }, []);

  useEffect(() => {
    const query = requestedCompanyId ? `?company_id=${encodeURIComponent(requestedCompanyId)}` : '';

    axios.get(`${endPoint()}/api/campaign_manager/session${query}`, {withCredentials:true})
      .then(({data}) => {
        setSession(data);
        setSelectedCompanyId(String(data.company.id));
        setError('');
      })
      .catch(handleRequestError);
  }, [handleRequestError, requestedCompanyId]);

  useEffect(() => {
    if(!selectedCompanyId || activeSection !== 'pathway') return;

    setLoading(true);
    const params = new URLSearchParams({company_id:selectedCompanyId, limit:'100'});
    if(stage) params.set('stage', stage);
    if(search) params.set('search', search);

    axios.get(`${endPoint()}/api/campaign_manager/pathways?${params.toString()}`, {withCredentials:true})
      .then(({data}) => {
        setPathway(data);
        setError('');
        setLoading(false);
      })
      .catch(handleRequestError);
  }, [activeSection, handleRequestError, search, selectedCompanyId, stage]);

  useEffect(() => {
    if(!selectedCompanyId || activeSection !== 'campaigns') return;

    setCampaignsLoading(true);
    const params = new URLSearchParams({company_id:selectedCompanyId, limit:'100'});
    if(campaignSearch) params.set('search', campaignSearch);

    axios.get(`${endPoint()}/api/campaign_manager/campaigns?${params.toString()}`, {withCredentials:true})
      .then(({data}) => {
        setCampaigns(data);
        setError('');
        setCampaignsLoading(false);
      })
      .catch(handleRequestError);
  }, [activeSection, campaignSearch, handleRequestError, selectedCompanyId]);

  useEffect(() => {
    if(!selectedCompanyId || activeSection !== 'compose') return;

    setComposeLoading(true);
    const params = new URLSearchParams({company_id:selectedCompanyId});

    axios.get(`${endPoint()}/api/campaign_manager/message_runs?${params.toString()}`, {withCredentials:true})
      .then(({data}) => {
        setMessageRuns(data);
        setError('');
        setComposeLoading(false);
      })
      .catch(handleRequestError);
  }, [activeSection, composeRefresh, handleRequestError, selectedCompanyId]);

  useEffect(() => {
    if(!selectedCompanyId || activeSection !== 'scheduled') return;

    setScheduledLoading(true);
    const params = new URLSearchParams({company_id:selectedCompanyId});
    Object.entries(scheduledFilters).forEach(([key, value]) => {
      if(value) params.set(key, value);
    });

    axios.get(`${endPoint()}/api/campaign_manager/scheduled_items?${params.toString()}`, {withCredentials:true})
      .then(({data}) => {
        setScheduledItems(data);
        setError('');
        setScheduledLoading(false);
      })
      .catch(handleRequestError);
  }, [activeSection, handleRequestError, scheduledFilters, selectedCompanyId]);

  useEffect(() => {
    if(activeSection !== 'compose' || !messageRuns?.message_runs?.some((run) => ['queued', 'in_progress'].includes(run.status))) return undefined;

    const timer = window.setTimeout(() => setComposeRefresh((current) => current + 1), 5000);
    return () => window.clearTimeout(timer);
  }, [activeSection, messageRuns]);

  const activeMembership = session?.companies?.find((company) => String(company.id) === selectedCompanyId) || session?.company;
  const stages = stageOrder.map((key) => pathway?.stages?.find((item) => item.key === key) || {key, label:key, count:0, percent:0});

  const selectCompany = (event) => {
    const companyId = event.target.value;
    setSelectedCompanyId(companyId);
    setStage('');
    setSearch('');
    setSearchInput('');
    setCampaignSearch('');
    setCampaignSearchInput('');
    setScheduledSearchInput('');
    setScheduledFilters({channel:'', source:'', status:'', search:''});
    setPathway(null);
    setCampaigns(null);
    setMessageRuns(null);
    setScheduledItems(null);
    setComposeNotice('');
    window.history.replaceState({}, '', `/campaign-manager?company_id=${encodeURIComponent(companyId)}`);
  };

  const submitSearch = (event) => {
    event.preventDefault();
    setSearch(searchInput.trim());
  };

  const submitCampaignSearch = (event) => {
    event.preventDefault();
    setCampaignSearch(campaignSearchInput.trim());
  };

  const submitScheduledSearch = (event) => {
    event.preventDefault();
    setScheduledFilters((current) => ({...current, search:scheduledSearchInput.trim()}));
  };

  const updateScheduledFilter = (key, value) => {
    setScheduledFilters((current) => ({...current, [key]:value}));
  };

  if(error && !session){
    return (
      <main className="campaign-manager-state">
        <div className="campaign-manager-state__card">
          <div className="campaign-manager-brand-mark">HSL</div>
          <h1>Campaign Manager</h1>
          <p>{error}</p>
          <a href="/patients">Return to HSC</a>
        </div>
      </main>
    );
  }

  return (
    <div className="campaign-manager-shell">
      <header className="campaign-manager-header">
        <div className="campaign-manager-brand">
          <div className="campaign-manager-brand-mark">HSL</div>
          <div>
            <strong>Campaign Manager</strong>
            <span>Study communications workspace</span>
          </div>
        </div>
        <div className="campaign-manager-header__actions">
          {session?.companies?.length > 1 ?
            <label className="campaign-manager-company-select">
              <span>Study</span>
              <select value={selectedCompanyId} onChange={selectCompany}>
                {session.companies.map((company) => <option key={company.id} value={company.id}>{company.name}</option>)}
              </select>
            </label>
            :
            <div className="campaign-manager-study">
              <span>Study</span>
              <strong>{activeMembership?.name || 'Loading…'}</strong>
            </div>
          }
          <span className="campaign-manager-role">{activeMembership?.role_label}</span>
          <a className="campaign-manager-back" href="/patients">Back to HSC</a>
        </div>
      </header>

      <nav className="campaign-manager-nav" aria-label="Campaign Manager sections">
        <button className={activeSection === 'pathway' ? 'is-active' : ''} onClick={() => setActiveSection('pathway')} type="button">Pathway</button>
        <button className={activeSection === 'campaigns' ? 'is-active' : ''} onClick={() => setActiveSection('campaigns')} type="button">Campaigns</button>
        <a href={`/manage/campaigns${selectedCompanyId ? `?company_id=${encodeURIComponent(selectedCompanyId)}` : ''}`}>Campaign setup</a>
        <button className={activeSection === 'compose' ? 'is-active' : ''} onClick={() => setActiveSection('compose')} type="button">Compose</button>
        <button className={activeSection === 'scheduled' ? 'is-active' : ''} onClick={() => setActiveSection('scheduled')} type="button">Scheduled</button>
        <a
          href={`${endPoint()}/api/event_tracker_sso`}
          rel="noopener noreferrer"
          target="_blank"
          title="Open the scoped UAT audit dashboard in a new tab"
        >
          UAT event tracker <span aria-hidden="true">↗</span>
        </a>
      </nav>

      {activeSection === 'pathway' ? <main className="campaign-manager-main">
        <section className="campaign-manager-intro">
          <div>
            <p className="campaign-manager-eyebrow">Recruitment operations</p>
            <h1>Study pathway</h1>
            <p>See where contacts are waiting across recruitment, enrollment, engagement, and retention.</p>
          </div>
          <div className="campaign-manager-slice-note">
            <span className="campaign-manager-slice-note__dot" />
            Read-only QA slice · operational IDs only
          </div>
        </section>

        {error ? <div className="campaign-manager-alert">{error}</div> : null}

        <section className="campaign-manager-stages" aria-label="Pathway stage summary">
          {stages.map((item, index) => (
            <button
              className={`campaign-manager-stage campaign-manager-stage--${item.key} ${stage === item.key ? 'is-selected' : ''}`}
              key={item.key}
              onClick={() => setStage(stage === item.key ? '' : item.key)}
              type="button"
            >
              <div className="campaign-manager-stage__topline">
                <span>0{index + 1}</span>
                <span>{item.percent}%</span>
              </div>
              <strong>{item.count}</strong>
              <h2>{item.label}</h2>
              <div className="campaign-manager-stage__bar"><span style={{width:`${item.percent}%`}} /></div>
            </button>
          ))}
        </section>

        <section className="campaign-manager-queue">
          <div className="campaign-manager-queue__header">
            <div>
              <p className="campaign-manager-eyebrow">Prioritized follow-up</p>
              <h2>{stage ? `${stages.find((item) => item.key === stage)?.label} work queue` : 'All contacts'}</h2>
              <span>{pathway?.pagination?.total || 0} contacts in this view</span>
            </div>
            <form onSubmit={submitSearch} className="campaign-manager-search">
              <label htmlFor="campaign-manager-search">Search operational ID</label>
              <div>
                <input
                  id="campaign-manager-search"
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Participant, site, or EDC ID"
                  type="search"
                  value={searchInput}
                />
                <button type="submit">Search</button>
              </div>
            </form>
          </div>

          {loading ?
            <div className="campaign-manager-loading"><span /> Loading pathway…</div>
            :
            <div className="campaign-manager-table-wrap">
              <table className="campaign-manager-table">
                <thead>
                  <tr>
                    <th>Contact</th>
                    <th>Flow / tier</th>
                    <th>Current stage</th>
                    <th>Bottleneck</th>
                    <th>Next action</th>
                    <th>Priority</th>
                    <th>Last activity</th>
                  </tr>
                </thead>
                <tbody>
                  {pathway?.contacts?.map((contact) => (
                    <tr key={contact.id}>
                      <td>
                        <strong>Participant {contact.participant_id}</strong>
                        <span>{[contact.site_id && `Site ${contact.site_id}`, contact.edc_id && `EDC ${contact.edc_id}`].filter(Boolean).join(' · ') || 'No secondary ID'}</span>
                      </td>
                      <td><strong>{contact.flow || '—'}</strong><span>{contact.tier ? `Tier ${contact.tier}` : 'No tier'}</span></td>
                      <td><span className={`campaign-manager-stage-pill campaign-manager-stage-pill--${contact.current_stage}`}>{contact.stage_label}</span><small>{contact.stage_status}</small></td>
                      <td>{contact.bottleneck || <span className="campaign-manager-muted">No active blocker</span>}</td>
                      <td>{contact.next_action}</td>
                      <td><span className={`campaign-manager-priority campaign-manager-priority--${contact.priority}`}>{contact.priority}</span></td>
                      <td>{formatDate(contact.last_activity_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {pathway?.contacts?.length === 0 ?
                <div className="campaign-manager-empty">No contacts match this view.</div>
                : null}
            </div>
          }
        </section>
      </main> : null}

      {activeSection === 'campaigns' ? <main className="campaign-manager-main">
        <section className="campaign-manager-intro">
          <div>
            <p className="campaign-manager-eyebrow">Campaign attribution</p>
            <h1>Campaigns</h1>
            <p>View the existing HSC campaign catalog and registration activity in one workspace.</p>
          </div>
          <div className="campaign-manager-slice-note">
            <span className="campaign-manager-slice-note__dot" />
            Read-only · no contact details
          </div>
        </section>

        {error ? <div className="campaign-manager-alert">{error}</div> : null}

        <section className="campaign-manager-metrics" aria-label="Campaign metric summary">
          <div><span>Total campaigns</span><strong>{campaigns?.summary?.campaigns || 0}</strong><small>{campaigns?.summary?.campaigns_with_activity || 0} with activity</small></div>
          <div><span>Tracked visits</span><strong>{campaigns?.summary?.visits || 0}</strong><small>Campaign landing activity</small></div>
          <div><span>Identified contacts</span><strong>{campaigns?.summary?.identified_contacts || 0}</strong><small>Distinct registered contacts</small></div>
          <div><span>Identification rate</span><strong>{campaigns?.summary?.identified_rate || 0}%</strong><small>Identified contacts / visits</small></div>
        </section>

        <section className="campaign-manager-queue campaign-manager-campaigns">
          <div className="campaign-manager-queue__header">
            <div>
              <p className="campaign-manager-eyebrow">HSC campaign catalog</p>
              <h2>Campaign performance</h2>
              <span>{campaigns?.pagination?.total || 0} campaigns in this view</span>
            </div>
            <form onSubmit={submitCampaignSearch} className="campaign-manager-search">
              <label htmlFor="campaign-manager-campaign-search">Search campaigns</label>
              <div>
                <input
                  id="campaign-manager-campaign-search"
                  onChange={(event) => setCampaignSearchInput(event.target.value)}
                  placeholder="Title, description, or UTM"
                  type="search"
                  value={campaignSearchInput}
                />
                <button type="submit">Search</button>
              </div>
            </form>
          </div>

          {campaignsLoading ?
            <div className="campaign-manager-loading"><span /> Loading campaigns…</div>
            :
            <div className="campaign-manager-table-wrap">
              <table className="campaign-manager-table campaign-manager-campaign-table">
                <thead>
                  <tr>
                    <th>Campaign</th>
                    <th>Audience</th>
                    <th>Attribution</th>
                    <th>Visits</th>
                    <th>Identified</th>
                    <th>Rate</th>
                    <th>Channels</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns?.campaigns?.map((campaign) => (
                    <tr key={campaign.id}>
                      <td>
                        <strong>{campaign.title || `Campaign ${campaign.id}`}</strong>
                        <span>{campaign.description || 'No description'}</span>
                        {campaign.shortened_url ? <a href={campaign.shortened_url} rel="noopener noreferrer" target="_blank">Open campaign link</a> : null}
                      </td>
                      <td>{campaign.group_name || <span className="campaign-manager-muted">All study contacts</span>}</td>
                      <td><strong>{campaign.utm_source || '—'}</strong><span>{campaign.utm_campaign || 'No UTM campaign'}</span></td>
                      <td><strong className="campaign-manager-number">{campaign.metrics.visits}</strong></td>
                      <td><strong className="campaign-manager-number">{campaign.metrics.identified_contacts}</strong></td>
                      <td><span className="campaign-manager-rate">{campaign.metrics.identified_rate}%</span></td>
                      <td><span>Web {campaign.metrics.channels.web}</span><span>iOS {campaign.metrics.channels.ios} · Android {campaign.metrics.channels.android}</span></td>
                      <td>{formatDate(campaign.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {campaigns?.campaigns?.length === 0 ?
                <div className="campaign-manager-empty">No campaigns match this view.</div>
                : null}
            </div>
          }
        </section>

        <p className="campaign-manager-metric-note">These metrics reflect existing HSC campaign-link attribution. Delivery, open, click, bounce, and complaint metrics will attach to AWS SES message runs in the sending slice.</p>
      </main> : null}

      {activeSection === 'compose' ? <main className="campaign-manager-main">
        <section className="campaign-manager-intro">
          <div>
            <p className="campaign-manager-eyebrow">Manual batch communications</p>
            <h1>Compose communications</h1>
            <p>Build targeted email, SMS, and push drafts, review the live preview, then send a confirmed manual batch.</p>
          </div>
          <div className="campaign-manager-slice-note">
            <span className="campaign-manager-slice-note__dot" />
            Draft → Ready → Sent
          </div>
        </section>

        {error ? <div className="campaign-manager-alert">{error}</div> : null}
        {composeNotice ? <div className="campaign-manager-success">{composeNotice}</div> : null}

        {composeLoading && !messageRuns ?
          <div className="campaign-manager-loading"><span /> Loading composer…</div>
          :
          <MessageComposer
            messageRuns={messageRuns}
            onError={setError}
            onMessageRuns={setMessageRuns}
            onNotice={setComposeNotice}
            onRefresh={() => setComposeRefresh((current) => current + 1)}
            onRequestError={handleRequestError}
            selectedCompanyId={selectedCompanyId}
          />
        }
      </main> : null}

      {activeSection === 'scheduled' ? <main className="campaign-manager-main">
        <section className="campaign-manager-intro">
          <div>
            <p className="campaign-manager-eyebrow">Cross-channel operations</p>
            <h1>Scheduled communications</h1>
            <p>Review upcoming and historical Campaign Manager, HSC Scheduler, SMS batch, and push activity in one company-scoped view.</p>
          </div>
          <div className="campaign-manager-slice-note">
            <span className="campaign-manager-slice-note__dot" />
            Read-only · metric availability shown
          </div>
        </section>

        {error ? <div className="campaign-manager-alert">{error}</div> : null}

        <ScheduledCommunications
          filters={scheduledFilters}
          inventory={scheduledItems}
          loading={scheduledLoading}
          onFilterChange={updateScheduledFilter}
          onSearch={submitScheduledSearch}
          searchInput={scheduledSearchInput}
          setSearchInput={setScheduledSearchInput}
        />
      </main> : null}
    </div>
  );
};

export default CampaignManagerApp;
